import { computed, ref, watch } from "vue";

import type { UseMenuKeyboardOptions, UseMenuKeyboardReturn } from "./types";

export function useMenuKeyboard({
  hide,
  items,
  menuId,
  onSelect,
  show,
  visible,
}: UseMenuKeyboardOptions): UseMenuKeyboardReturn {
  const activeIndex = ref<number>(-1);

  const enabledIndices = computed<number[]>(() =>
    items.value.reduce<number[]>((acc, item, idx) => {
      if (!item.disabled) acc.push(idx);

      return acc;
    }, [])
  );

  const activeDescendantId = computed<string | undefined>(() =>
    items.value.length && visible.value && activeIndex.value >= 0
      ? `${menuId.value}-item-${activeIndex.value}`
      : undefined
  );

  function itemId(index: number): string {
    return `${menuId.value}-item-${index}`;
  }

  function stepActiveIndex(direction: 1 | -1): void {
    const enabled = enabledIndices.value;
    if (!enabled.length) return;
    const currentPos = enabled.indexOf(activeIndex.value);

    const nextPos = (currentPos + direction + enabled.length) % enabled.length;

    activeIndex.value = enabled[nextPos];
  }

  function typeAheadJump(char: string): void {
    const enabled = enabledIndices.value;
    if (!enabled.length) return;
    const lower = char.toLowerCase();
    const startPos = enabled.indexOf(activeIndex.value) + 1;

    for (let offset = 0; offset < enabled.length; offset += 1) {
      const idx = enabled[(startPos + offset) % enabled.length];

      if (items.value[idx].label.toLowerCase().startsWith(lower)) {
        activeIndex.value = idx;

        return;
      }
    }
  }

  function activateActiveItem(): void {
    if (activeIndex.value < 0) return;
    const item = items.value[activeIndex.value];
    if (!item || item.disabled) return;
    onSelect(item, activeIndex.value);
    hide();
  }

  function openMenuAt(index: number): void {
    activeIndex.value = index;
    show();
  }

  function onMenuKeydown(event: KeyboardEvent): void {
    const enabled = enabledIndices.value;
    if (!items.value.length || !enabled.length) return;
    const first = enabled[0];
    const last = enabled[enabled.length - 1];
    const { key } = event;

    if (!visible.value) {
      if (key === "Enter" || key === " " || key === "ArrowDown") {
        event.preventDefault();
        openMenuAt(first);
      } else if (key === "ArrowUp") {
        event.preventDefault();
        openMenuAt(last);
      }

      return;
    }

    switch (key) {
      case "ArrowDown":
        event.preventDefault();
        stepActiveIndex(1);

        return;
      case "ArrowUp":
        event.preventDefault();
        stepActiveIndex(-1);

        return;
      case "Home":
        event.preventDefault();
        activeIndex.value = first;

        return;
      case "End":
        event.preventDefault();
        activeIndex.value = last;

        return;
      case "Enter":
      case " ":
        event.preventDefault();
        activateActiveItem();

        return;
      case "Escape":
        event.preventDefault();
        hide();

        return;
      case "Tab":
        hide();

        return;
      default:
        break;
    }

    if (key.length === 1 && /\S/.test(key)) {
      event.preventDefault();
      typeAheadJump(key);
    }
  }

  function onItemClick(index: number): void {
    const item = items.value[index];
    if (!item || item.disabled) return;
    activeIndex.value = index;
    onSelect(item, index);
    hide();
  }

  function onItemHover(index: number): void {
    if (items.value[index]?.disabled) return;
    activeIndex.value = index;
  }

  watch(
    visible,
    (isOpen) => {
      if (!items.value.length) return;

      if (isOpen) {
        if (activeIndex.value < 0) {
          activeIndex.value = enabledIndices.value[0] ?? -1;
        }
      } else {
        activeIndex.value = -1;
      }
    },
    { immediate: true }
  );

  return {
    activeDescendantId,
    activeIndex,
    itemId,
    onItemClick,
    onItemHover,
    onMenuKeydown,
  };
}
